import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { Helmet } from 'react-helmet';
import { useFormik } from 'formik';
import 'src/App.css';
import {
  Box,
  Button,
  Link,
  Snackbar,
  TextField,
  Typography,
  Container,
  Alert,
  InputAdornment,
  Autocomplete
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useDispatch, useSelector } from 'react-redux';

import { registerAdmin } from 'src/api';
import { commonReducerAction } from 'src/redux/reducers/common';
import { loaderReducerAction } from 'src/redux/reducers/loader';
import ImagePicker from 'src/components/imagePicker';
import { registerValidationSchema } from 'src/modals/validations/registerValidationSchema';
import { getMobileNumber } from 'src/utils/mobileNumberValidator';
import { COUNTRY_CURRENCY_CODES } from 'src/commons/currency';
import './style.css';

const Register = () => {
  const dispatch = useDispatch();
  const { startLoading, stopLoading } = loaderReducerAction;
  const { updateUserCredentials, setVisible ,setGymId } = commonReducerAction;
  const [imageFile, setImageFile] = useState(null);
  const { visible } = useSelector((state) => state.common);

  const [snackbarState, setSnackbarState] = useState({
    open: false,
    message: '',
    severity: ''
  });

  const initialValues = {
    email: '',
    name: '',
    gymName: '',
    password: '',
    gymAddress: '',
    gymPhone: '',
    gymCallingCode: '',
    callingCode: '',
    logo: null,
    currency: ''
  };

  const {
    errors,
    handleBlur,
    handleChange,
    handleSubmit,
    isSubmitting,
    touched,
    setFieldValue,
    values
  } = useFormik({
    initialValues,
    validationSchema: registerValidationSchema,
    mode: 'onSubmit',
    onSubmit: async (values) => {
      try {
        const formData = new FormData();
        formData.append('email', values.email);
        formData.append('password', values.password);
        formData.append('full_name', values?.name);
        formData.append('device_type', 0);
        formData.append('device_token', 'device_token');
        formData.append('gym_name', values.gymName);
        formData.append('gym_address', values.gymAddress);
        formData.append('currency', values.currency);

        if (values?.gymPhone && values?.gymCallingCode) {
          const mobileNumber = getMobileNumber(
            values?.gymPhone,
            values?.gymCallingCode
          );
          formData.append('gym_phone', mobileNumber);
          formData.append('gym_calling_code', values.gymCallingCode);
        }

        if (values?.phone && values?.callingCode) {
          const mobileNumber = getMobileNumber(
            values?.phone,
            values?.callingCode
          );
          formData.append('phone', mobileNumber);
          formData.append('calling_code', values?.callingCode);
        }
        if (values?.gymEmail) {
          formData.append('gym_email', values.gymEmail);
        }
        if (values?.logo) {
          formData.append('logo', values.logo);
        }
        dispatch(startLoading());
        const res = await registerAdmin(formData);
        dispatch(stopLoading());
        const isError = res.status !== 200;
        setSnackbarState({
          open: true,
          message: res.message,
          severity: isError ? 'error' : 'success'
        });
        if (!isError) {
          dispatch(setGymId(res?.data?.all_gyms[0]._id));
          dispatch(updateUserCredentials(res.data));
        }
      } catch (err) {
        setSnackbarState({
          open: true,
          message: err.message,
          severity: 'error'
        });
      }
    }
  });

  const handlePhoneInputChange = (value, country, phoneKey, callingCodeKey) => {
    setFieldValue(phoneKey, value);
    setFieldValue(callingCodeKey, `+${country?.dialCode}`);
  };

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    setSnackbarState({
      ...snackbarState,
      open: false
    });
  };

  return (
    <>
      <Helmet>
        <title>Register | GMS</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center'
        }}
      >
        <Container maxWidth="sm">
          <form
            onSubmit={handleSubmit}
            style={{ marginTop: '2rem', marginBottom: '2rem' }}
            noValidate
          >
            <Box sx={{ mb: 3, height: 'auto' }}>
              <Typography color="textPrimary" variant="h2" align="center">
                Create new account
              </Typography>
            </Box>
            <div className="relative">
              <input
                label="Logo"
                type="file"
                accept="image/*"
                className="hide"
                id="logo"
                name="logo"
                onBlur={handleBlur}
                onChange={(event) => {
                  setFieldValue('logo', event?.currentTarget?.files[0]);

                  const file = event?.currentTarget?.files[0];
                  if (file) {
                    const fileImage = new FileReader();
                    fileImage.readAsDataURL(file);
                    fileImage.onload = () => {
                      setImageFile(fileImage.result);
                    };
                  }
                }}
              />
              <label for="logo" className="absolute">
                <ImagePicker
                  pickedImage={imageFile}
                  setImage={setImageFile}
                  containerClass="borderRadius"
                  placeholder="Upload logo"
                  hideCancel
                />
              </label>
            </div>

            <Box sx={{ mb: 1, height: 'auto' }}>
              <Typography color="textPrimary" variant="h3" fontWeight={600}>
                Gym Info
              </Typography>
            </Box>
            <div>
              <TextField
                error={Boolean(touched.gymName && errors.gymName)}
                fullWidth
                required
                helperText={touched.gymName && errors.gymName}
                label="Gym Name"
                margin="normal"
                name="gymName"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.gymName}
                variant="outlined"
                inputProps={{ sx: { backgroundColor: 'white' } }}
              />
              <TextField
                error={Boolean(touched.gymAddress && errors.gymAddress)}
                fullWidth
                required
                helperText={touched.gymAddress && errors.gymAddress}
                label="Gym Address"
                margin="normal"
                name="gymAddress"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.gymAddress}
                variant="outlined"
                inputProps={{ sx: { backgroundColor: 'white' } }}
              />
              <Box sx={{ marginBottom: '8px' }}>
                <PhoneInput
                  country={'us'}
                  placeholder="Gym Phone Number"
                  inputStyle={{
                    height: '3.5rem',
                    width: '100%',
                    border:
                      touched.gymPhone && errors?.gymPhone
                        ? '1px solid red'
                        : '1px solid rgba(0, 0, 0, 0.23)',
                    marginTop: '16px'
                  }}
                  buttonClass={
                    touched.gymPhone && errors?.gymPhone
                      ? 'errorDropdown'
                      : 'flagDropdown'
                  }
                  value={values?.gymPhone}
                  name="gymPhone"
                  onChange={(value, country) =>
                    handlePhoneInputChange(
                      value,
                      country,
                      'gymPhone',
                      'gymCallingCode'
                    )
                  }
                  inputProps={{
                    required: true
                  }}
                  searchPlaceholder="Search"
                  enableSearch
                  countryCodeEditable={false}
                />
                {touched.gymPhone && errors?.gymPhone && (
                  <p
                    style={{
                      color: 'red',
                      fontFamily: 'Roboto',
                      fontSize: '0.8rem',
                      marginLeft: '15px',
                      marginTop: '5px'
                    }}
                  >
                    {errors?.gymPhone}
                  </p>
                )}
              </Box>
              <TextField
                error={Boolean(touched.gymEmail && errors.gymEmail)}
                fullWidth
                helperText={touched.gymEmail && errors.gymEmail}
                label="Gym Email"
                margin="normal"
                name="gymEmail"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.gymEmail}
                variant="outlined"
                inputProps={{ sx: { backgroundColor: 'white' } }}
              />

              <Autocomplete
                id="country-select-demo"
                sx={{
                  width: '100%',
                  marginTop: '16px',
                  marginBottom: '8px'
                }}
                options={COUNTRY_CURRENCY_CODES}
                autoHighlight
                name="currency"
                required
                getOptionLabel={(option) => option.code}
                onInputChange={(e, value) => setFieldValue('currency', value)}
                inputProps={{ sx: { backgroundColor: 'white' } }}
                containerClass="inputStyle"
                renderOption={(props, option) => {
                  const { key, ...optionProps } = props;
                  return (
                    <Box
                      key={key}
                      component="li"
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%'
                      }}
                      {...optionProps}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          width: '100%'
                        }}
                      >
                        <Typography
                          sx={{ marginRight: 3 }}
                        >{`(${option.symbol})`}</Typography>
                        <Typography>{option.code}</Typography>
                      </div>
                    </Box>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    label="Currency"
                    required
                    name="currency"
                    error={Boolean(touched?.currency && errors.currency)}
                    helperText={touched?.currency && errors.currency}
                    fullWidth
                    variant="outlined"
                    {...params}
                    slotProps={{
                      htmlInput: {
                        ...params.inputProps,
                        autoComplete: 'new-password'
                      }
                    }}
                  />
                )}
              />
            </div>

            <Box sx={{ mt: 3, mb: 1, height: 'auto' }}>
              <Typography color="textPrimary" variant="h3" fontWeight={600}>
                Gym Owner Info
              </Typography>
            </Box>
            <TextField
              error={Boolean(touched.name && errors.name)}
              fullWidth
              required
              helperText={touched.name && errors.name}
              label="Your Name"
              margin="normal"
              name="name"
              onBlur={handleBlur}
              onChange={handleChange}
              value={values.name}
              variant="outlined"
              inputProps={{ sx: { backgroundColor: 'white' } }}
            />
            <Box sx={{ marginBottom: '8px' }}>
              <PhoneInput
                country={'us'}
                placeholder="Mobile Number"
                inputStyle={{
                  height: '3.5rem',
                  width: '100%',
                  border:
                    touched.phone && errors?.phone
                      ? '1px solid red'
                      : '1px solid rgba(0, 0, 0, 0.23)',
                  marginTop: '16px'
                }}
                buttonClass={
                  touched.phone && errors?.phone
                    ? 'errorDropdown'
                    : 'flagDropdown'
                }
                value={values?.phone}
                name="phone"
                onChange={(value, country) =>
                  handlePhoneInputChange(value, country, 'phone', 'callingCode')
                }
                inputProps={{
                  required: true
                }}
                searchPlaceholder="Search"
                enableSearch
                countryCodeEditable={false}
              />
              {touched?.phone && errors?.phone && (
                <p
                  style={{
                    color: 'red',
                    fontFamily: 'Roboto',
                    fontSize: '0.8rem',
                    marginLeft: '15px',
                    marginTop: '5px'
                  }}
                >
                  {errors?.phone}
                </p>
              )}
            </Box>

            <TextField
              error={Boolean(touched.email && errors.email)}
              fullWidth
              required
              helperText={touched.email && errors.email}
              label="Email"
              margin="normal"
              name="email"
              onBlur={handleBlur}
              onChange={handleChange}
              type="email"
              value={values.email}
              variant="outlined"
              inputProps={{ sx: { backgroundColor: 'white' } }}
            />
            <TextField
              error={Boolean(touched.password && errors.password)}
              fullWidth
              helperText={touched.password && errors.password}
              label="Password"
              required
              margin="normal"
              name="password"
              onBlur={handleBlur}
              onChange={handleChange}
              type={visible ? 'text' : 'password'}
              value={values.password}
              variant="outlined"
              InputProps={{
                sx: { backgroundColor: 'white' },
                endAdornment: (
                  <InputAdornment
                    position="end"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => {
                      dispatch(setVisible());
                    }}
                  >
                    {visible ? (
                      <VisibilityIcon
                        sx={{
                          color: 'black',
                          height: '18px'
                        }}
                      />
                    ) : (
                      <VisibilityOffIcon
                        sx={{
                          color: 'black',
                          height: '18px'
                        }}
                      />
                    )}
                  </InputAdornment>
                )
              }}
            />

            <Box sx={{ py: 2 }}>
              <Button
                color="primary"
                disabled={isSubmitting}
                fullWidth
                size="large"
                type="submit"
                variant="contained"
              >
                Sign up now
              </Button>
            </Box>
            <Typography color="textSecondary" variant="body1">
              Have an account?{' '}
              <Link component={RouterLink} to="/login" variant="h6">
                Sign in
              </Link>
            </Typography>
          </form>
          <Snackbar
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            open={snackbarState.open}
            autoHideDuration={3000}
            onClose={handleClose}
          >
            <div>
              <Alert onClose={handleClose} severity={snackbarState.severity}>
                {snackbarState.message}
              </Alert>
            </div>
          </Snackbar>
        </Container>
      </Box>
    </>
  );
};

export default Register;
